import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import { useNotification } from '../../contexts/NotificationContext';
import ProtectedRoute from '../../components/ProtectedRoute';
import AdminLayout, { useTheme } from '../../components/AdminLayout';

function SeatsContent() {
  const { t } = useTranslation();
  const { showAlert, showConfirm } = useNotification();
  const { T, S } = useTheme();
  const [seats, setSeats] = useState([]);
  const [shops, setShops] = useState([]);
  const [allBranches, setAllBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [selectedShopId, setSelectedShopId] = useState('');
  const [form, setForm] = useState({ shop_id: '', branch_id: '', name: '' });

  const filteredBranches = allBranches.filter(b => !selectedShopId || b.shop_id === parseInt(selectedShopId));

  const fetchData = async () => {
    try {
      const [seatData, shopData, branchData] = await Promise.all([
        api.getAdminSeats(),
        api.getAdminShops(),
        api.getBranches(),
      ]);
      setSeats(seatData);
      setShops(shopData);
      setAllBranches(branchData);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const branchById = (id) => allBranches.find(b => b.id === id);
  const shopById = (id) => shops.find(s => s.id === id);
  // A seat may have shop_id directly OR be linked via branch_id
  const salonOfSeat = (seat) => {
    if (seat.shop_id) return shopById(seat.shop_id)?.name ?? '—';
    if (seat.branch_id) {
      const b = branchById(seat.branch_id);
      return shopById(b?.shop_id)?.name ?? '—';
    }
    return '—';
  };
  const branchOfSeat = (seat) => seat.branch_id ? (branchById(seat.branch_id)?.name ?? '—') : '—';

  const openCreate = () => {
    setEditing('create');
    setSelectedShopId('');
    setForm({ shop_id: '', branch_id: '', name: '' });
  };

  const openEdit = (s) => {
    const shopId = s.shop_id ?? branchById(s.branch_id)?.shop_id ?? '';
    setSelectedShopId(String(shopId));
    setEditing(s.id);
    setForm({ shop_id: s.shop_id ?? '', branch_id: s.branch_id ?? '', name: s.name });
  };

  const handleShopChange = (shopId) => {
    setSelectedShopId(shopId);
    setForm(f => ({ ...f, shop_id: shopId, branch_id: '' }));
  };

  const save = async () => {
    if (!selectedShopId) { showAlert(t('admin.pleaseSelectSalon')); return; }
    try {
      const payload = {
        shop_id: parseInt(selectedShopId),
        branch_id: form.branch_id ? parseInt(form.branch_id) : null,
        name: form.name,
      };
      if (editing === 'create') await api.createAdminSeat(payload);
      else await api.updateAdminSeat(editing, { name: form.name });
      setEditing(null); fetchData();
    } catch (err) { showAlert(err.message); }
  };

  const remove = async (id) => {
    showConfirm(t('admin.deleteSeatConfirm'), async () => {
      try { await api.deleteAdminSeat(id); fetchData(); }
      catch (err) { showAlert(err.message); }
    });
  };

  return (
    <AdminLayout role="admin">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '2rem' }}>
        <div>
          <h1 style={S.pageTitle}>{t('admin.seats')}</h1>
          <div style={{ width: '40px', height: '2px', background: T.orange }} />
        </div>
        <button style={S.btnOrange} onClick={openCreate}
          onMouseEnter={e => e.target.style.background = T.orangeHov}
          onMouseLeave={e => e.target.style.background = T.orange}>+ {t('admin.addSeat')}</button>
      </div>

      {editing && (
        <div style={S.modal}>
          <div style={S.modalBox}>
            <h2 style={S.modalTitle}>{editing === 'create' ? t('admin.newSeat') : t('admin.editSeat')}</h2>

            <label style={{ display: 'block', fontSize: '0.75rem', color: T.muted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.4rem' }}>1. {t('admin.selectSalon')}</label>
            <select value={selectedShopId} onChange={e => handleShopChange(e.target.value)} style={S.select}>
              <option value="">{t('admin.chooseSalon')}</option>
              {shops.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>

            <label style={{ display: 'block', fontSize: '0.75rem', color: T.muted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.4rem' }}>2. {t('admin.selectBranchOptional')}</label>
            <select value={form.branch_id} onChange={e => setForm({ ...form, branch_id: e.target.value })} style={S.select} disabled={!selectedShopId}>
              <option value="">{selectedShopId ? t('admin.noSpecificBranch') : t('admin.selectSalonFirst')}</option>
              {filteredBranches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>

            <input placeholder={t('admin.seatNamePlaceholder')} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={S.input} />

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={save} style={{ ...S.btnOrange, flex: 1 }}>{t('common.save')}</button>
              <button onClick={() => setEditing(null)} style={S.btnOutline}>{t('common.cancel')}</button>
            </div>
          </div>
        </div>
      )}

      {loading ? <p style={{ color: T.muted }}>{t('common.loading')}</p> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={S.table}>
            <thead>
              <tr>{[t('admin.id'), t('admin.name'), t('admin.salon'), t('admin.branch'), t('admin.actions')].map((h, i) => <th key={i} style={S.th}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {seats.map(s => (
                <tr key={s.id}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(27,213,173,0.04)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <td style={S.td}>{s.id}</td>
                  <td style={{ ...S.td, fontWeight: 500, color: T.white }}>{s.name}</td>
                  <td style={{ ...S.td, color: T.orange, fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', letterSpacing: '0.05em' }}>{salonOfSeat(s)}</td>
                  <td style={S.td}>{branchOfSeat(s)}</td>
                  <td style={S.td}>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button style={S.btnSmall} onClick={() => openEdit(s)}
                        onMouseEnter={e => e.target.style.color = T.white}
                        onMouseLeave={e => e.target.style.color = T.muted}>{t('common.edit')}</button>
                      <button style={S.btnDanger} onClick={() => remove(s.id)}>{t('common.delete')}</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}

export default function Seats() {
  return <ProtectedRoute requireSuperAdmin><SeatsContent /></ProtectedRoute>;
}
